import { useEffect, useState } from "react";

import { useTranslation } from "@/core/i18n";
import AiStarIcon from "atoms/icon/icons/ai-start-icon.svg";

import adonisAdvisorImage from "../../assets/adonis.webp";
import backgroundImage from "../../assets/ai-advisor-screen-bg.webp";
import ariaAdvisorImage from "../../assets/aria.webp";
import { PersonalisationHeading } from "../PersonalisationHeading";
import { PersonalisationScreenLayout } from "../PersonalisationScreenLayout";

interface AdvisorItem {
  id: string;
  label: string;
}

interface ChooseAdvisorProps {
  options?: AdvisorItem[];
  onContinue?: (selection: AdvisorItem | null) => void;
  selectedColor?: string;
  continueButtonText?: string;
  progressPercentage?: number;
  onBack?: () => void;
  onClose?: () => void;
  delayMs?: number;
  initialSelectedId?: string | null;
}

// Persona names ("Aria", "Adonis") are brand identities and are not translated.
const defaultAdvisorOptions: AdvisorItem[] = [
  { id: "aria", label: "Aria" },
  { id: "adonis", label: "Adonis" },
];

export const ChooseYourAIAdvisor = ({
  options = defaultAdvisorOptions,
  onContinue,
  continueButtonText,
  initialSelectedId,
  delayMs = 150,
}: ChooseAdvisorProps) => {
  const { t } = useTranslation();
  const resolvedContinueButtonText = continueButtonText ?? t("common.continue");
  const [selected, setSelected] = useState<AdvisorItem | null>(
    initialSelectedId ? (options.find((o) => o.id === initialSelectedId) ?? null) : null,
  );

  const [screenState, setScreenState] = useState<"loading" | "ready">("loading");

  useEffect(() => {
    const id = setTimeout(() => setScreenState("ready"), delayMs);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectionChange = (item: AdvisorItem | null) => {
    setSelected(item);
  };

  const handleContinue = () => {
    onContinue?.(selected);
  };

  const hasSelection = selected !== null;

  return (
    <PersonalisationScreenLayout
      className="!pb-0 !pt-0"
      contentClassName="flex flex-1 flex-col overflow-x-hidden pb-[calc(env(safe-area-inset-bottom)+2rem)] pt-[calc(env(safe-area-inset-top)+2rem)] !px-0"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="flex flex-col items-center gap-2 px-4">
        <AiStarIcon className="h-8 w-8" />
        <PersonalisationHeading size="md" customSize="24px" className="text-center">
          {t("chooseYourAIAdvisor.headingLine1")} <br />
          {t("chooseYourAIAdvisor.headingLine2")}
        </PersonalisationHeading>
      </div>

      <div
        className="relative flex-1 transition-opacity duration-300 ease-in-out"
        style={{
          opacity: screenState === "ready" ? 1 : 0,
        }}
      >
        <img
          data-role="adonis-advisor-image-on-top-of-overlay"
          className="absolute z-30 h-auto w-[70vw] max-w-[275px] transition-opacity duration-300 ease-in-out"
          style={{
            top: -25,
            left: 6,
            opacity: selected?.id === "aria" ? 0 : 1,
          }}
          src={adonisAdvisorImage}
          alt={t("chooseYourAIAdvisor.advisorAlt", { name: "Adonis" })}
        />
        <img
          data-role="adonis-advisor-image-under-overlay"
          className="absolute z-10 h-auto w-[70vw] max-w-[275px]"
          style={{
            top: -25,
            left: 6,
          }}
          src={adonisAdvisorImage}
          alt={t("chooseYourAIAdvisor.advisorAlt", { name: "Adonis" })}
        />
        <img
          data-role="aria-advisor-image-on-top-of-overlay"
          className="absolute z-30 h-auto w-[48vw] max-w-[190px] transition-opacity duration-300 ease-in-out"
          style={{
            top: 0,
            right: -12,
            opacity: selected?.id === "adonis" ? 0 : 1,
          }}
          src={ariaAdvisorImage}
          alt={t("chooseYourAIAdvisor.advisorAlt", { name: "Aria" })}
        />
        <img
          data-role="aria-advisor-image-under-overlay"
          className="absolute z-10 h-auto w-[48vw] max-w-[190px]"
          style={{
            top: 0,
            right: -12,
          }}
          src={ariaAdvisorImage}
          alt={t("chooseYourAIAdvisor.advisorAlt", { name: "Aria" })}
        />
        <div
          data-role="overlay-when-selection-present"
          className="z-20 transition-opacity duration-300 ease-in-out"
          style={{
            position: "fixed",
            background:
              "linear-gradient(180deg, rgba(102, 102, 102, 0.00) -3.62%, rgba(0, 0, 0, 0.44) 34.52%)",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            opacity: hasSelection ? 1 : 0,
          }}
        />
      </div>
      <div
        className="fixed bottom-0 z-40 h-80 w-full"
        style={{
          background: "linear-gradient(to bottom, transparent, #000000)",
        }}
      />

      <div className="z-40 flex flex-col items-center gap-8 px-6">
        <div className="flex w-full items-center justify-around gap-4">
          <button
            onClick={() => handleSelectionChange({ id: "adonis", label: "Adonis" })}
            className={`flex h-9 min-w-[120px] max-w-[144px] flex-1 items-center justify-center rounded-full px-4 font-sans text-base font-normal transition-all duration-300 ease-in-out ${
              selected?.id === "adonis"
                ? "bg-[#bda280] text-white"
                : "bg-white/75 text-nearly-black shadow-subtle"
            }`}
          >
            Adonis
          </button>
          <button
            onClick={() => handleSelectionChange({ id: "aria", label: "Aria" })}
            className={`flex h-9 min-w-[120px] max-w-[144px] flex-1 items-center justify-center rounded-full px-4 font-sans text-base font-normal transition-all duration-300 ease-in-out ${
              selected?.id === "aria"
                ? "bg-[#bda280] text-white"
                : "bg-white/75 text-nearly-black shadow-subtle"
            }`}
          >
            Aria
          </button>
        </div>

        <p className="text-center font-sans text-body1 text-white">
          {t("chooseYourAIAdvisor.advisorAvailability")}
        </p>
        <button
          className={`flex h-12 w-full items-center justify-center rounded-full px-8 font-sans text-button font-normal transition-all duration-300 ease-in-out ${
            hasSelection
              ? "bg-[#BDA280] text-white opacity-100 hover:bg-[#b29671] active:opacity-60"
              : "bg-[#BDA280] text-white opacity-20"
          }`}
          disabled={!hasSelection}
          onClick={handleContinue}
        >
          {resolvedContinueButtonText}
        </button>
      </div>
    </PersonalisationScreenLayout>
  );
};
